import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import UserOpenEntityAgreement from "legal-entities/agreements/open/user-open-entity-agreement";
import { AuthResponse } from "users/_api/auth-email-api-service";
import { AuthContext } from "users/auth/context/auth-context";
import AuthInfoSchema, { AuthInfoJSON, AuthInfoParams } from "users/auth/info/auth-info-schema";
import UserAuthToken from "users/auth/tokens/user-auth-token";
import UserInformation from "users/information/user-information";
import User from "users/user";

export class AuthInfo {
  public static readonly EXAMPLE: AuthInfo = AuthInfo.create({
    authToken: UserAuthToken.EXAMPLE,
    userInformation: UserInformation.EXAMPLE,
  });

  public static create(values: AuthInfoParams): AuthInfo {
    const parsed = AuthInfoSchema.validate(values);
    return new AuthInfo(parsed);
  }

  public static fromJSON(json: unknown): AuthInfo | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = AuthInfoSchema.deserialize(json);
    return new AuthInfo(params);
  }

  public readonly authToken: UserAuthToken;
  public readonly userInformation: UserInformation;

  public constructor(params: AuthInfoParams) {
    this.authToken = UserAuthToken.create(params.authToken);
    this.userInformation = UserInformation.create(params.userInformation);
  }

  public toJSON(): AuthInfoJSON {
    return AuthInfoSchema.serialize(this);
  }

  public get user(): User {
    return User.create({
      id: this.userInformation.id,
      name: this.userInformation.name,
      email: this.userInformation.email,
      legalEntityId: this.userInformation.legalEntityId,
      companyName: this.userInformation.companyName,
      isSuperUser: this.userInformation.isSuperUser,
      individualId: this.userInformation.individualId,
      hasSigningAuthority: this.userInformation.hasSigningAuthority,
    });
  }

  public get context(): AuthContext {
    return AuthContext.create(this.userInformation);
  }

  public get openEntityAgreements(): UserOpenEntityAgreement[] {
    return this.userInformation.openEntityAgreements.map((data) => {
      return UserOpenEntityAgreement.create(data);
    });
  }

  public toAuthResponse(): AuthResponse {
    return {
      authToken: this.authToken,
      user: this.user,
      context: this.context,
      openEntityAgreements: this.openEntityAgreements,
    };
  }

  public isEqualTo(other: AuthInfo): boolean {
    return this.authToken.isEqualTo(other.authToken) && this.userInformation.isEqualTo(other.userInformation);
  }

  public toString(): string {
    return `AuthInfo { 
      authToken: ${this.authToken.toString()},
      userInformation: ${this.userInformation.toString()}
    }`;
  }
}
